import React from 'react';
import {
  Row, Spinner,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'components/DesignSystem/Button';

import Accordion from 'components/Accordion';
import Checkbox from 'components/DesignSystem/Checkbox';
import { useDeletePntPreferencesMutation, useListPntPreferencesQuery } from 'services/api/Meteorology';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import useFromUnixTime from 'utils/hooks/useFromUnixTime';
import useNumericForecastCP from 'utils/hooks/meteorology/useNumericForecastControlPanel';
import useSavePreferencesModal from 'utils/hooks/meteorology/useSavePreferencesModal';
import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import useVariables from 'hooks/Meteorology/Fields/useVariables';
import useLoadFirstPreference from 'hooks/Meteorology/Fields/NumericForecast/useLoadFirstPreference';
import DatePicker from 'components/DatePicker';
import ConfirmationModal from 'components/PreferenceDialog/Success';
import ErrorModal from 'components/PreferenceDialog/Error';
import VisualizationSelector from 'components/Meteorology/Fields/ControlPanel/VisualizationSelector';
import {
  requestConsult,
  selectMFControlPanelViewMode,
  setViewMode,
} from 'redux/reducers/meteorologyFieldsControlPanelReducer';
import { isHeatmapVariable } from 'helper/products/meteorology/heatmapModels';
import { MeteorologyViewMode } from 'redux/reducers/meteorologyFieldsObservedDataControlPanelReducer';
import PreferencesAccordion from '../PreferencesAccordion';
import RegionButton from './RegionButton';
import PeriodButton from './PeriodButton';
import SavePreferencesModal from './SavePreferencesModal';
import VariablesAccordionItem from './VariablesAccordionItem';
import ModelsAccordionItem from './ModelsAccordionItem';
import UseSetNumericForecastDefaults from './UseSetNumericForecastDefaults';

export default function NumericForecast() {
  const dispatch = useDispatch();
  const viewMode = useSelector(selectMFControlPanelViewMode);
  const { models, getModelOptions, isFetching } = useControlPanelOptions('pnt');
  const { variables, selectableModels } = useVariables(models, getModelOptions, viewMode);
  const {
    setShowFormModal,
    isShowingConfirmationModal,
    setShowConfirmationModal,
    isShowingErrorModal,
    setShowErrorModal,
    error: saveError,
  } = useSavePreferencesModal();
  const { data, error } = useListPntPreferencesQuery();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const preferenceList = data?.data || [];
  const [onPreferenceDeleteTrigger, {
    isLoading: preferenceDeleteLoading,
    error: preferenceDeleteError,
  }] = useDeletePntPreferencesMutation();
  useHandleRequestErrors(error as ResponseError | undefined);
  useHandleRequestErrors(preferenceDeleteError as ResponseError | undefined);

  const {
    region,
    models: selectedModels,
    period,
    variable,

    forecastUnixDate,
    setForecastUnixDate,
    resetPreferences,
    setPeriod,
    setModelArray,
    setRegion,
    anomaly,
    setAnomaly,
    controlPanelUsed,
    setVariable,
    clearModelSelections,
  } = useNumericForecastCP();

  const selectedDate = useFromUnixTime(forecastUnixDate);

  useLoadFirstPreference({
    data,
    preferenceList,
    resetPreferences,
    setPeriod,
    setModelArray,
    setRegion,
    setAnomaly,
    setForecastUnixDate,
    controlPanelUsed,
    setVariable,
    setViewMode: (mode) => dispatch(setViewMode(mode)),
    requestConsult: () => dispatch(requestConsult()),
  });
  UseSetNumericForecastDefaults({
    data,
    preferenceList,
    controlPanelUsed,

    region,
    selectedModels,
    period,
    variable,
    anomaly,
    forecastUnixDate,

    setRegion,
    setModelArray,
    setPeriod,
    setVariable,
    setAnomaly,
    setForecastUnixDate,
    requestConsult: () => dispatch(requestConsult()),
  });

  if (isFetching) {
    return (
      <div className="text-center py-3">
        <Spinner />
      </div>
    );
  }

  const isHeatmap = viewMode === 'heatmap';
  const isHeatmapDisabled = !variable || !isHeatmapVariable(variable);

  const baseSpatialParamsReady = Boolean(
    region && selectedModels && selectedModels.length > 0 && period && forecastUnixDate,
  );
  const heatmapParamsReady = Boolean(
    variable && forecastUnixDate && selectedModels && selectedModels.length > 0,
  );
  const areParamsSelected = isHeatmap ? heatmapParamsReady : baseSpatialParamsReady;

  const handleViewModeChange = (next: MeteorologyViewMode) => {
    dispatch(setViewMode(next));
  };

  const handleRunQuery = () => {
    dispatch(requestConsult());
  };

  return (
    <div>
      <ConfirmationModal
        open={isShowingConfirmationModal}
        closing={() => { setShowConfirmationModal(false); }}
      />
      <ErrorModal
        open={isShowingErrorModal}
        message={saveError}
        closing={() => { setShowErrorModal(false); }}
      />
      <SavePreferencesModal />
      <Accordion defaultActiveKey="0">
        <PreferencesAccordion
          preferenceList={preferenceList}
          deleteTrigger={onPreferenceDeleteTrigger}
          deleteLoading={preferenceDeleteLoading}
        />
        <VariablesAccordionItem
          variables={variables}
          eventKey="1"
        />
        <Accordion.Item eventKey="2">
          <Accordion.Header>Visualização</Accordion.Header>
          <Accordion.Body className="d-grid">
            <VisualizationSelector
              value={viewMode}
              onChange={handleViewModeChange}
              options={[
                { value: 'heatmap', label: 'Heatmap', disabled: isHeatmapDisabled },
                { value: 'spatial', label: 'Espacial' },
              ]}
            />
          </Accordion.Body>
        </Accordion.Item>
        <ModelsAccordionItem
          selectableModels={selectableModels}
          onAllClicked={() => setModelArray(selectableModels.map((m) => m.value))}
          onNoneClicked={clearModelSelections}
          eventKey="3"
        />
        <Accordion.Item eventKey="4">
          <Accordion.Header>Data de previsão</Accordion.Header>
          <Accordion.Body className="d-grid">
            <DatePicker
              inline
              selected={selectedDate}
              onChange={setForecastUnixDate}
              maxDate={new Date()}
            />
          </Accordion.Body>
        </Accordion.Item>
        {!isHeatmap && (
          <Accordion.Item eventKey="5">
            <Accordion.Header>Área de plotagem</Accordion.Header>
            <Accordion.Body className="d-grid">
              <RegionButton text="Brasil" value="BR" />
              <RegionButton text="Sul" value="S" />
              <RegionButton text="Sudeste" value="SE" />
              <RegionButton text="Centro-Oeste" value="CO" />
              <RegionButton text="Nordeste" value="NE" />
              <RegionButton text="Norte" value="N" />
            </Accordion.Body>
          </Accordion.Item>
        )}
        {!isHeatmap && (
          <Accordion.Item eventKey="6">
            <Accordion.Header>Período de seleção</Accordion.Header>
            <Accordion.Body className="d-grid">
              <PeriodButton text="Diário" value="d+0" />
              <PeriodButton text="Pentada" value="p+0" />
              <PeriodButton text="Semanal" value="s+0" />
              <PeriodButton text="Mensal" value="m+0" />
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>
      {!isHeatmap && (
        <Checkbox
          id="anomaly-checkbox"
          className="mt-4"
          label="Anomalia"
          checked={anomaly}
          onChange={() => { setAnomaly(!anomaly); }}
        />
      )}
      <Row className="pt-3">
        <Button
          className="w-100 mt-5"
          onClick={handleRunQuery}
          disabled={!areParamsSelected}
        >
          Realizar consulta
        </Button>
      </Row>
      <Row className="pt-3">
        <Button
          onClick={() => {
            setShowFormModal(true);
          }}
        >
          Salvar nova preferência
        </Button>
      </Row>
      <Row>
        <Button type="tertiary" className="clear-filters mt-1" onClick={resetPreferences}>
          Limpar filtros
        </Button>
      </Row>
    </div>
  );
}
